import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Input } from 'antd'
import * as Rules from 'admin/lib/ValidationRules'
import { DualTimeZoneDateTimePicker } from 'admin/pages/RecordEdit/common/DualTimeZoneDateTimePicker'

import {
  Section,
  SectionProps,
  FormItem,
  AutoCompleteFormItem,
  FormSpace
} from 'admin/lib/Forms'
import {
  compareDatesWithOffSets,
  usDateFormatter
} from 'admin/lib/Functions/timeZoneHelper'
import { Delivery as fields } from 'admin/lib/Records'

import { SupplierSelect } from './SupplierSelect'
import { useDateAutoCopy } from 'admin/pages/RecordEdit/common/useDateAutoCopy'
import { KnownAddressInput } from 'admin/pages/RecordEdit/common/KnownAddressInput'
import {
  CheckAddress,
  CheckConsignmentOrDeliveryNumber,
  CheckNotes,
  CheckReceiverOrSupplierOrSenderOrCarrierReference,
  CheckTradeContractor,
  CheckVehicleReg
} from 'admin/lib/SimpleDataValidation'

type Props = {
  createdAt: string
  projectName: string
  projectTimeZone: string
  projectCountry: string
  projectId: string
  isImperial: boolean
  hasOneOrMoreLineItemWithProductGroupsContainingConcrete: boolean
} & Omit<SectionProps, 'children'>

export const DeliveryData = (props: Props) => {
  const form = useFormContext()

  // Register non-interactive fields set from setValue
  useEffect(() => {
    form.register(fields.data.deliveryAddressEntered.path)
    form.register(fields.data.deliveryAddressCoordinates.path)
    form.register(fields.data.dispatchAddressEntered.path)
    form.register(fields.data.dispatchAddressCoordinates.path)
  }, [form])

  const journeyDistanceMiles = form.watch('data.journey.distanceMiles')
  // Not ideal - but known address does not populate in time otherwise
  const knownDispatchAddress = form.watch(fields.data.dispatchAddressKnown.path)

  const exitDatePopulation = useDateAutoCopy(
    fields.data.deliveryArrivalDate.path,
    fields.data.deliveryExitDate.path
  )

  const [arrivalDateWarningMessage, setArrivalDateWarningMessage] = useState<
    string | null
  >(null)
  const [exitDateWarningMessage, setExitDateWarningMessage] = useState<
    string | null
  >(null)

  const DATE_WARNING_MESSAGE =
    'Entered date is after the Creation Date for this record'
  const DATE_BEFORE_ARRIVAL_WARNING_MESSAGE =
    'Entered Exit date is before the Arrival Date for this record'

  const validateExitDate = (value: string) => {
    if (
      props.createdAt &&
      compareDatesWithOffSets(value, props.projectTimeZone, props.createdAt)
    ) {
      setExitDateWarningMessage(DATE_WARNING_MESSAGE)
    } else if (
      (value ||
        props.hasOneOrMoreLineItemWithProductGroupsContainingConcrete) &&
      new Date(value) <
        new Date(form.getValues(fields.data.deliveryArrivalDate.path))
    ) {
      setExitDateWarningMessage(DATE_BEFORE_ARRIVAL_WARNING_MESSAGE)
    } else {
      setExitDateWarningMessage(null)
    }
    // Always return TRUE so we do not block form submission
    return true
  }

  const validateArrivalDate = (value: string) => {
    if (
      props.createdAt &&
      compareDatesWithOffSets(value, props.projectTimeZone, props.createdAt)
    ) {
      setArrivalDateWarningMessage(DATE_WARNING_MESSAGE)
    } else {
      setArrivalDateWarningMessage(null)
    }

    // Trigger Exit date validation too:
    validateExitDate(form.getValues(fields.data.deliveryExitDate.path))
    // Always return TRUE so we do not block form submission
    return true
  }

  useEffect(() => {
    let sub1, sub2
    if (props.isImperial) {
      sub1 = form.watch('data.deliveryArrivalDate')
      sub2 = form.watch('data.deliveryExitDate')
      form.setValue(
        'data.deliveryArrivalDateUS',
        usDateFormatter(form.getValues('data.deliveryArrivalDate'))
      )
      form.setValue(
        'data.deliveryExitDateUS',
        usDateFormatter(form.getValues('data.deliveryExitDate'))
      )
    }
    return () => {
      if (sub1 && sub1.unsubscribe) {
        sub1.unsubscribe()
      }
      if (sub2 && sub2.unsubscribe) {
        sub2.unsubscribe()
      }
    }
  }, [form, props.isImperial])

  return (
    <Section {...props}>
      {({ firstInputRef }) => (
        <>
          <DualTimeZoneDateTimePicker
            id="DeliveryDataArrivalDate"
            label={fields.data.deliveryArrivalDate.label}
            name={fields.data.deliveryArrivalDate.path}
            imperialName={fields.data.deliveryArrivalDateUS.path}
            isImperial={props.isImperial}
            form={form}
            firstInputRef={firstInputRef}
            validateDate={validateArrivalDate}
            warningMessage={arrivalDateWarningMessage}
          />

          <DualTimeZoneDateTimePicker
            id="DeliveryDataExitDate"
            label={fields.data.deliveryExitDate.label}
            name={fields.data.deliveryExitDate.path}
            imperialName={fields.data.deliveryExitDateUS.path}
            isImperial={props.isImperial}
            form={form}
            validateDate={validateExitDate}
            warningMessage={exitDateWarningMessage}
            onFocus={() => {
              exitDatePopulation.onFocus()
              form.trigger(fields.data.deliveryExitDate.path)
            }}
          />

          <FormItem
            label={fields.data.deliveryId.label}
            name={fields.data.deliveryId.path}
            id="deliveryDataDeliveryId"
            rules={Rules.MandatoryString}
            simpleDataValidationRules={CheckConsignmentOrDeliveryNumber}
          />

          <FormItem
            label={fields.data.deliveryAddress.label}
            name={fields.data.deliveryAddress.path}
            id="deliveryDataDeliveryAddress"
            simpleDataValidationRules={v => CheckAddress(v, null)}
          >
            <Input.TextArea autoSize rows={1} />
          </FormItem>

          {journeyDistanceMiles > 1000 && (
            <p className="validation-warning">
              Calculated journey distance exceeds recommended limit. Please
              confirm addresses are correct.
            </p>
          )}

          <KnownAddressInput
            form={form}
            addressName="Delivery"
            knownlabel={fields.data.deliveryAddressKnown.label}
            knownPropertyPath={fields.data.deliveryAddressKnown.path}
            givenPropertyPath={fields.data.deliveryAddress.path}
            enteredPropertyPath={fields.data.deliveryAddressEntered.path}
            coordinatesPropertyPath={
              fields.data.deliveryAddressCoordinates.path
            }
            projectCountry={props.projectCountry}
          />

          <FormItem
            label={fields.data.givenDispatchAddress.label}
            name={fields.data.givenDispatchAddress.path}
            id="deliveryDataDispatchAddress"
            simpleDataValidationRules={v =>
              CheckAddress(v, knownDispatchAddress)
            }
          >
            <Input.TextArea autoSize rows={1} />
          </FormItem>
          <KnownAddressInput
            form={form}
            addressName="Dispatch"
            knownlabel={fields.data.dispatchAddressKnown.label}
            knownPropertyPath={fields.data.dispatchAddressKnown.path}
            givenPropertyPath={fields.data.givenDispatchAddress.path}
            enteredPropertyPath={fields.data.dispatchAddressEntered.path}
            coordinatesPropertyPath={
              fields.data.dispatchAddressCoordinates.path
            }
            projectCountry={props.projectCountry}
          />

          <AutoCompleteFormItem
            label={fields.data.tradeContractor.label}
            name={fields.data.tradeContractor.path}
            id="deliveryDataTradeContractor"
            simpleDataValidationRules={v =>
              CheckTradeContractor(v, props.projectName)
            }
          />

          <FormSpace />

          <SupplierSelect />

          <AutoCompleteFormItem
            label={fields.data.vehicleRegistration.label}
            name={fields.data.vehicleRegistration.path}
            id="deliveryDataVehicleReg"
            simpleDataValidationRules={CheckVehicleReg}
            uppercaseInput={true}
          />

          <FormSpace />

          <AutoCompleteFormItem
            label={fields.data.receiverReferenceNumber.label}
            name={fields.data.receiverReferenceNumber.path}
            id="deliveryDataReceiverReference"
            simpleDataValidationRules={
              CheckReceiverOrSupplierOrSenderOrCarrierReference
            }
          />

          <AutoCompleteFormItem
            label={fields.data.supplierReferenceNumber.label}
            name={fields.data.supplierReferenceNumber.path}
            id="deliveryDataSupplierReference"
            simpleDataValidationRules={
              CheckReceiverOrSupplierOrSenderOrCarrierReference
            }
          />

          <AutoCompleteFormItem
            label={fields.data.notes.label}
            name={fields.data.notes.path}
            id="deliveryDataNotes"
            simpleDataValidationRules={CheckNotes}
          >
            <Input.TextArea autoSize rows={1} />
          </AutoCompleteFormItem>
        </>
      )}
    </Section>
  )
}
